import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { usePageMeta } from "@/hooks/usePageMeta";
import { allEpisodes, searchEpisodes } from "@/lib/catalog";

const hotKeywords = ["搬家", "旧照片", "画室", "雨夜", "大结局", "治愈"];

export default function SearchPage() {
  usePageMeta(
    "搜索剧集 - 朋友的妈妈4影视站",
    "在朋友的妈妈4影视站内搜索剧集章节，支持按剧集标题、剧情简介与标签关键字检索。",
    "朋友的妈妈4搜索,朋友的妈妈4剧情搜索,朋友的妈妈4剧集检索",
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const queryParam = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(queryParam);

  useEffect(() => {
    setKeyword(queryParam);
  }, [queryParam]);

  const results = useMemo(() => searchEpisodes(queryParam), [queryParam]);

  const submitSearch = (value: string) => {
    const trimmed = value.trim();
    if (trimmed) {
      setSearchParams({ q: trimmed });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="max-w-[1280px] mx-auto px-4 md:px-8 pt-24 md:pt-28 pb-10">
        <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-foreground-600">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-900">搜索</span>
        </nav>

        <header className="mt-6">
          <h1 className="font-heading text-3xl md:text-4xl tracking-wide text-foreground-950">
            站内搜索
          </h1>
          <p className="mt-3 text-sm leading-7 text-foreground-500 max-w-2xl">
            输入关键字检索剧集标题、剧情简介与标签内容。按回车或点击搜索按钮开始查找。
          </p>
        </header>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            submitSearch(keyword);
          }}
          className="mt-6 rounded-lg bg-background-100 border border-background-200/70 p-3 md:p-4"
        >
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="relative flex-1 min-w-0">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
              <input
                type="text"
                name="q"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索剧集名称、剧情关键字，例如：搬家 / 画室"
                aria-label="搜索关键字"
                className="w-full h-11 pl-9 pr-10 rounded-md bg-background-200/60 border border-background-300/60 text-sm text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 box-border"
              />
              {keyword && (
                <button
                  type="button"
                  aria-label="清除关键字"
                  onClick={() => {
                    setKeyword("");
                    setSearchParams({});
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md text-foreground-500 hover:text-foreground-900 hover:bg-background-300/50 flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-sm" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-foreground-600">热门关键字</span>
            {hotKeywords.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => {
                  setKeyword(word);
                  submitSearch(word);
                }}
                className="px-2.5 h-6 rounded-full bg-background-200/70 hover:bg-accent-100 text-[11px] text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
              >
                {word}
              </button>
            ))}
          </div>
        </form>

        {queryParam && (
          <div className="mt-5 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-foreground-600">当前搜索词</span>
            <span className="inline-flex items-center gap-2 h-7 pl-3 pr-2 rounded-full bg-primary-500/15 border border-primary-500/40 text-foreground-950">
              {queryParam}
              <button
                type="button"
                aria-label="清除当前搜索"
                onClick={() => {
                  setKeyword("");
                  setSearchParams({});
                }}
                className="w-5 h-5 rounded-full bg-background-200/70 hover:bg-background-300 text-foreground-800 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-[11px]" />
              </button>
            </span>
            <span className="text-foreground-600">
              共找到 {results.length} 条结果
            </span>
          </div>
        )}

        {!queryParam ? (
          <div className="mt-6 rounded-lg bg-background-100 border border-background-200/70 p-6 md:p-8">
            <h2 className="font-heading text-lg text-foreground-950">试试从这些内容开始</h2>
            <p className="mt-2 text-sm text-foreground-500">
              全站共收录 {allEpisodes.length} 集，以下为最近更新的章节。
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {allEpisodes.slice(-4).map((episode) => (
                <Link
                  key={episode.id}
                  to={`/detail/${episode.id}`}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-background-200/60 hover:bg-background-300/60 text-xs text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span className="font-label text-[10px] text-accent-500">
                    EP {String(episode.number).padStart(2, "0")}
                  </span>
                  {episode.title}
                </Link>
              ))}
            </div>
          </div>
        ) : results.length === 0 ? (
          <div className="mt-6 rounded-lg bg-background-100 border border-background-200/70 py-16 px-6 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-background-200/70 text-foreground-500 flex items-center justify-center">
              <i className="ri-search-eye-line text-2xl" />
            </div>
            <h2 className="mt-5 font-heading text-lg text-foreground-950">
              没有找到与「{queryParam}」相关的内容
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              试试更换关键字，或从下方的章节入口直接浏览。
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              {["搬家", "旧照片", "画室", "大结局"].map((word) => (
                <button
                  key={word}
                  type="button"
                  onClick={() => {
                    setKeyword(word);
                    submitSearch(word);
                  }}
                  className="h-8 px-3 rounded-full bg-background-200/70 hover:bg-accent-100 text-xs text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {word}
                </button>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/episodes"
                className="h-10 px-5 inline-flex items-center rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览全部剧集
              </Link>
              <Link
                to="/"
                className="h-10 px-5 inline-flex items-center rounded-md border border-background-300/60 text-foreground-800 text-sm hover:border-primary-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 space-y-3">
              {results.map((episode, index) => (
                <Link
                  key={episode.id}
                  to={`/detail/${episode.id}`}
                  className="group flex gap-3 md:gap-4 rounded-lg bg-background-100 border border-background-200/70 p-3 hover:border-primary-500/60 transition-colors cursor-pointer anim-fade-up"
                  style={{ animationDelay: `${index * 40}ms` }}
                >
                  <div className="w-[84px] h-[120px] md:w-[96px] md:h-[140px] shrink-0 rounded-md overflow-hidden bg-background-900">
                    <img
                      src={episode.cover}
                      alt={episode.title}
                      title={`${episode.title} 搜索结果`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1 py-1">
                    <div className="flex items-center gap-2">
                      <span className="font-label text-[10px] tracking-wider text-accent-500">
                        EP {String(episode.number).padStart(2, "0")}
                      </span>
                      <span className="text-[10px] text-foreground-600">
                        {episode.releaseDate}
                      </span>
                    </div>
                    <h2 className="mt-1 text-sm md:text-base font-medium text-foreground-950 line-clamp-1">
                      {episode.title}
                    </h2>
                    <p className="mt-1.5 text-xs md:text-sm leading-6 text-foreground-500 line-clamp-2">
                      {episode.synopsis}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      {episode.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 h-5 rounded-full bg-secondary-100 text-secondary-900 text-[10px] flex items-center"
                        >
                          {tag}
                        </span>
                      ))}
                      <span className="text-[11px] text-foreground-600 ml-auto hidden sm:inline-flex items-center gap-1">
                        查看详情
                        <i className="ri-arrow-right-line" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <aside className="lg:col-span-4">
              <div className="rounded-lg bg-background-100 border border-background-200/70 p-4">
                <h2 className="font-heading text-sm tracking-wide text-foreground-950 mb-3">
                  搜索结果说明
                </h2>
                <ul className="space-y-2.5 text-xs leading-6 text-foreground-500">
                  <li className="flex gap-2">
                    <i className="ri-check-line text-accent-500 mt-0.5" />
                    检索范围覆盖剧集标题、章节简介、标签与剧情正文。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-check-line text-accent-500 mt-0.5" />
                    结果按集数顺序排列，点击任意条目进入详情页。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-check-line text-accent-500 mt-0.5" />
                    站内内容仅围绕「朋友的妈妈4」，不涉及其他作品。
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}